import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BlogPostShell, POSTS } from "@/pages/Blog";
import { CheckCircle2 } from "lucide-react";

const POST = POSTS.find((p) => p.slug === "score-a-competition-in-5-minutes");
const OG_IMAGE = "https://cameraclubscore.com/og/blog-score-a-competition-in-5-minutes.png";
const CANONICAL = "https://cameraclubscore.com/blog/score-a-competition-in-5-minutes";

// Feature-forward marketing voice. Aimed at prospective clubs shopping
// around. Uses concrete timings and a "you'll do X, then Y" structure
// so the reader can imagine themselves using it during a real session.
export default function PostScoreEntry() {
  return (
    <>
      <Helmet>
        <title>{`${POST.title} — Camera Club Score`}</title>
        <meta name="description" content={POST.excerpt} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content={POST.title} />
        <meta property="og:description" content={POST.excerpt} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={POST.title} />
        <meta name="twitter:description" content={POST.excerpt} />
        <meta name="twitter:image" content={OG_IMAGE} />
      </Helmet>
      <BlogPostShell post={POST}>
      <p className="lead">
        The whole point of software for camera clubs is that scoring
        night should feel effortless. Here's a walk-through of running
        one score entry session with Camera Club Score — from opening
        the app to publishing results — in five minutes flat.
      </p>

      <h2>Before the meeting: 60 seconds of prep</h2>

      <p>
        Log in, click <strong>Competitions</strong>, and hit{" "}
        <strong>Add competition</strong>. You need three things:
      </p>

      <ul>
        <li><strong>Name</strong> — "Round 3 — Nature Print" or whatever your programme card says.</li>
        <li><strong>Judge</strong> — the visiting judge's name, so it prints on the sheets and certificates automatically.</li>
        <li><strong>Type</strong> — Print or PDI. That's it.</li>
      </ul>

      <p>
        Save. The competition is now visible in the running order screen
        and the public portal. You're 40 seconds in.
      </p>

      <h2>Setting up the running order: 30 seconds</h2>

      <p>
        Open the competition, click <strong>Live Entry</strong>, and
        hit <strong>Add all photographers</strong>. Every active member
        appears in one click, pre-sorted by ID number.
      </p>

      <p>
        Prefer to control who's competing? Add them one at a time from
        the dropdown instead. Or if you've already got a running-order
        CSV from another system, upload it — Camera Club Score maps the
        columns for you.
      </p>

      <p>
        Once your list is set, click <strong>Lock order</strong>. This
        freezes the sequence so nothing shuffles around mid-session
        (nothing worse than the grid re-sorting when you're two thirds
        of the way through the pile). A padlock badge appears on the
        competition so everyone can see it's committed.
      </p>

      <h2>The judging session itself: however long the judge takes</h2>

      <p>
        This is where the tool earns its keep. The Live Entry grid is
        built around a single principle: the person on scoring duty
        shouldn't have to look at their hands.
      </p>

      <p>
        Press <strong>Enter</strong> and you jump straight to the next
        row's score field (skipping the image title, which the judge
        has usually already read out). Type the score. Enter. Type the
        next score. Enter. Enter. Enter.
      </p>

      <p>
        <strong>Every keystroke auto-saves.</strong> If the wifi drops,
        the app quietly queues changes and flushes them when you're back
        online — you never lose a score. If the browser crashes, refresh
        and you're back where you were.
      </p>

      <div className="callout">
        <div className="callout-title">The three keys you actually need</div>
        <ul>
          <li><strong>Enter</strong> — next row's score</li>
          <li><strong>Tab</strong> — next field in the same row (e.g. placement)</li>
          <li><strong>Up/Down arrows</strong> — jump between rows</li>
        </ul>
      </div>

      <p>
        When the judge picks their placements, tab across to the
        placement column and type <strong>1</strong>, <strong>2</strong>,
        <strong>3</strong>. Done. No dropdowns, no radio buttons — just
        keyboard.
      </p>

      <h2>After the last image: 30 seconds to results</h2>

      <p>
        Click <strong>Results</strong>. That's it — the leaderboard for
        this competition is already there. Photographers are ranked by
        score, ties are automatically marked, and placements are shown
        as coloured chips.
      </p>

      <p>
        You've got three things you probably want to do right now:
      </p>

      <ul>
        <li>
          <strong>Print the judge's sheet.</strong> One button on the
          Results page produces a clean PDF — score column, placement
          column, judge's name at the top. Handy for the club's paper
          records.
        </li>
        <li>
          <strong>Update the season league.</strong> Nothing to do.
          Season Stats already includes tonight's competition — the
          totals recalculated the moment you saved the last score.
        </li>
        <li>
          <strong>Share the results.</strong> The public portal has
          already updated. Anyone with your club's portal link can see
          tonight's results on their phone, no login required.
        </li>
      </ul>

      <h2>End of season: the certificates workflow that saves an evening</h2>

      <p>
        At the end of your season, head to <strong>Season Stats</strong>{" "}
        and click <strong>Certificates CSV</strong>. You get a
        ready-to-mail-merge CSV containing every image that hit the
        club's top mark, with columns for Code, Name, Competition,
        Image Title, Score, Judge, and Place.
      </p>

      <p>
        Open your certificate template in Word, point Mail Merge at the
        CSV, print. If Alice has three perfect-score images across the
        season, she gets three separate certificates — each with the
        correct image title and competition.
      </p>

      <p>
        Committees have told us this alone saves them a full evening
        every year. We believe them.
      </p>

      <h2>What we deliberately don't do</h2>

      <p>
        Camera Club Score doesn't upload images. It doesn't host photos.
        It doesn't manage payments, do EDIs, or handle inter-club
        battles (yet). We think there are already good tools for those
        jobs, and stacking too many features into one app is usually how
        good tools go bad.
      </p>

      <p>
        What we do — competition management, scoring, season standings,
        and a public portal — we try to do exceptionally well.
      </p>

      <h2>Try it with your next competition</h2>

      <ul className="feature-list">
        <li>
          <CheckCircle2 size={14} className="text-emerald-400" />
          Free for 30 days — no credit card required until you convert
        </li>
        <li>
          <CheckCircle2 size={14} className="text-emerald-400" />
          Set up in under two minutes with your existing photographers
          list
        </li>
        <li>
          <CheckCircle2 size={14} className="text-emerald-400" />
          Works on phones, tablets, and laptops — no app to install
        </li>
        <li>
          <CheckCircle2 size={14} className="text-emerald-400" />
          Export everything at any time; nothing is locked in
        </li>
      </ul>

      <p>
        Sign up at{" "}
        <Link to="/register" className="text-amber-400 hover:underline">
          cameraclubscore.com
        </Link>{" "}
        and try it with one competition. Worst case, you spend five
        minutes and go back to your spreadsheet with a clearer picture
        of what's possible. Best case, judging night gets a lot
        quieter.
      </p>
    </BlogPostShell>
    </>
  );
}
